import { useCallback, useEffect, useRef, useState } from 'react';
import { SCHEDULE_PUBLICATION_URL, SCHEDULE_REFRESH_MS } from '../../config/schedule';
import { fetchSchedule } from './scheduleData';

const CACHE_KEY = 'ubc-muaythai-schedule-v1';
function readCache() {
  try {
    const saved = JSON.parse(localStorage.getItem(CACHE_KEY));
    const data = saved?.data;
    if (saved?.source !== SCHEDULE_PUBLICATION_URL || !data?.tab?.name || !Number.isFinite(data.checkedAt) || !Number.isInteger(data.skippedRows) || !Array.isArray(data.sessions)) return null;
    if (!data.sessions.every(item => typeof item.id === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(item.date) && Number.isInteger(item.startMinutes) && item.startMinutes >= 0 && item.startMinutes < 1440 && Number.isInteger(item.endMinutes) && item.endMinutes >= 0 && item.endMinutes < 1440 && typeof item.location === 'string' && typeof item.classType === 'string' && typeof item.cancelled === 'boolean')) return null;
    return data;
  } catch { return null; }
}

export default function useSchedule() {
  const [data, setData] = useState(readCache);
  const [loading, setLoading] = useState(() => !readCache());
  const [error, setError] = useState('');
  const request = useRef(null);
  const checkedAt = useRef(data?.checkedAt || 0);
  const attemptedAt = useRef(0);

  const refresh = useCallback(async () => {
    if (request.current) return;
    const controller = new AbortController();
    request.current = controller;
    attemptedAt.current = Date.now();
    setLoading(true);
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const result = await fetchSchedule(controller.signal);
      if (request.current !== controller) return;
      setData(result);
      checkedAt.current = result.checkedAt;
      setError('');
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ source: SCHEDULE_PUBLICATION_URL, data: result })); } catch { /* Storage may be disabled; live reads still work. */ }
    } catch (failure) {
      if (request.current === controller) setError(failure.name === 'AbortError' ? 'The schedule request timed out.' : failure.message);
    } finally {
      clearTimeout(timeout);
      if (request.current === controller) {
        request.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const refreshIfDue = () => {
      if (document.visibilityState === 'visible' && Date.now() - Math.max(checkedAt.current, attemptedAt.current) >= SCHEDULE_REFRESH_MS) refresh();
    };
    refreshIfDue();
    const timer = setInterval(() => {
      refreshIfDue();
    }, 60 * 1000);
    const onVisible = () => {
      refreshIfDue();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      const controller = request.current;
      request.current = null;
      controller?.abort();
    };
  }, [refresh]);

  return { data, loading, error, refresh };
}
